/**
 * locationRegistry.cjs — Fuente única de verdad para slugs de ciudad/barrio
 *
 * Consumido por:
 *   - frontend/src/router.js
 *   - frontend/src/components/ColivingAdvancedSearch.vue
 *   - backend/scripts/generate-sitemap.js
 *   - backend/scripts/prerender-encuentratucoliving.js
 *
 * region: 'es' | 'us' | 'uk'
 */

'use strict';

/** @typedef {{ slug: string, name: string, region: 'es'|'us'|'uk', priority: string, hasStudents: boolean, hasEnglish: boolean, hasSpanish: boolean, prerender: boolean, esSearch: boolean, enSearch: boolean }} Location */

/** Slug canónico → slug canónico (redirect 301 en router ES) */
const SLUG_ALIASES = {
  'donostia': 'san-sebastian',
  'gipuzkoa': 'san-sebastian',
  'guipuzcoa': 'san-sebastian',
  'guipúzcoa': 'san-sebastian',
  'donosti': 'san-sebastian',
  'sansebastian': 'san-sebastian',
  'san-sebastián': 'san-sebastian',
  'málaga': 'malaga',
  'laspalmas': 'las-palmas',
  'gran-canaria': 'las-palmas',
  'grancanaria': 'las-palmas',
  'santa-cruz': 'tenerife',
  'santa-cruz-de-tenerife': 'tenerife',
};

/** Alias adicionales slug → nombre display (sin entrada propia en LOCATIONS) */
const SLUG_TO_NAME_EXTRAS = {
  'hounslow-central': 'Hounslow',
  'hounslow central': 'Hounslow',
  'westhampstead': 'West Hampstead',
  'nottinghill': 'Notting Hill',
  'florence-dock': 'Battersea',
  'the-palm-house': 'Harrow',
  'sunday-mills': 'Earlsfield',
  'westbournepark': 'Westbourne Park',
  'primrosehill': 'Primrose Hill',
  'belsizepark': 'Belsize Park',
  'elephantandcastle': 'Elephant and Castle',
  'claphamcommon': 'Clapham Common',
  'finsburypark': 'Finsbury Park',
  'maidavale': 'Maida Vale',
  'westkensington': 'West Kensington',
  'mileend': 'Mile End',
  'canadawater': 'Canada Water',
  'kingscross': 'Kings Cross',
  'claphamjunction': 'Clapham Junction',
  'eastputney': 'East Putney',
  'newmalden': 'New Malden',
  'royaldocks': 'Royal Docks',
  'canningtown': 'Canning Town',
  'stepneygreen': 'Stepney Green',
  'whitecity': 'White City',
  'londonbridge': 'London Bridge',
  'southbank': 'South Bank',
  'canarywharf': 'Canary Wharf',
  'whitechapel': 'Whitechapel',
  'bethnal-green': 'Bethnal Green',
  'stratford': 'Stratford',
  'holloway': 'Holloway',
  'earls-court': 'Earls Court',
  'deptford': 'Deptford',
  'southwark': 'Southwark',
  'highbury': 'Highbury',
  'tottenham': 'Tottenham',
  'greenwich': 'Greenwich',
  'hoxton': 'Hoxton',
  'vauxhall': 'Vauxhall',
  'aldgate': 'Aldgate',
  'bankside': 'Bankside',
  'charlton': 'Charlton',
  'brondesbury': 'Brondesbury',
  'acton': 'Acton',
  'new-cross': 'New Cross',
  'colindale': 'Colindale',
  'southall': 'Southall',
  'clerkenwell': 'Clerkenwell',
  'bow': 'Bow',
  'wapping': 'Wapping',
  'kentish-town': 'Kentish Town',
  'north-acton': 'North Acton',
  'south-bermondsey': 'South Bermondsey',
  'tufnell-park': 'Tufnell Park',
  'wembley-park': 'Wembley',
  'old-oak': 'Acton',
  'old-street': 'Shoreditch',
  'new-york-city': 'New York',
  'nyc': 'New York',
  'jerseycity': 'Jersey City',
  'queens': 'Queens',
  'bronx': 'Bronx',
};

/** Slugs con HTML estático prerenderizado (solo rutas de búsqueda por ciudad) */
const PRERENDER_SLUGS = new Set([
  // ES
  'madrid', 'barcelona', 'valencia', 'sevilla', 'malaga', 'salamanca', 'alicante',
  'granada', 'bilbao', 'zaragoza', 'las-palmas', 'tenerife',
  'pamplona',
  // US
  'new-york', 'manhattan', 'brooklyn', 'queens-nyc', 'jersey-city', 'williamsburg', 'astoria',
  // UK
  'london', 'wembley', 'royal-docks', 'poplar', 'canning-town', 'walthamstow',
  'stepney-green', 'lewisham', 'hayes', 'epsom', 'brixton', 'elephant-and-castle',
  'shoreditch', 'bloomsbury', 'kings-cross', 'canary-wharf', 'hackney',
  'islington', 'canada-water', 'limehouse', 'mile-end', 'clapham-junction', 'camden',
  'whitechapel', 'south-bank', 'hammersmith', 'stratford', 'bethnal-green', 'holloway',
  'southwark', 'earls-court',
]);

/**
 * Barrios UK con 5+ listings que merecen estar en el sitemap.
 * Los demás siguen prerendered y accesibles, pero no se señalan a Google
 * para evitar "Crawled, currently not indexed" en GSC por contenido thin.
 * Revisar cuando el inventario UK crezca (jul 2026: basado en seoCount de ColivingSearchSEO).
 */
const UK_SITEMAP_SLUGS = new Set([
  'london', 'elephant-and-castle', 'royal-docks', 'canning-town',
  'poplar', 'stepney-green', 'lewisham', 'hayes', 'epsom', 'camden',
]);

function loc(slug, name, region, opts = {}) {
  return {
    slug,
    name,
    region,
    priority: opts.priority ?? '0.7',
    hasStudents: opts.hasStudents ?? false,
    hasEnglish: opts.hasEnglish ?? (region !== 'es'),
    hasSpanish: opts.hasSpanish ?? (region === 'es'),
    prerender: opts.prerender ?? PRERENDER_SLUGS.has(slug),
    esSearch: opts.esSearch ?? (region === 'es'),
    enSearch: opts.enSearch ?? true,
  };
}

/** @type {Location[]} */
const LOCATIONS = [
  // ── ESPAÑA ──────────────────────────────────────────────────────────────
  loc('madrid', 'Madrid', 'es', { priority: '0.9', hasStudents: true, hasEnglish: true }),
  loc('barcelona', 'Barcelona', 'es', { priority: '0.9', hasStudents: true, hasEnglish: true }),
  loc('valencia', 'Valencia', 'es', { priority: '0.85', hasStudents: true, hasEnglish: true }),
  loc('sevilla', 'Sevilla', 'es', { priority: '0.85', hasStudents: true, hasEnglish: true }),
  loc('malaga', 'Málaga', 'es', { priority: '0.85', hasStudents: true, hasEnglish: true }),
  loc('bilbao', 'Bilbao', 'es', { priority: '0.8', hasStudents: true, hasEnglish: true }),
  // San Sebastián: sin inventario activo — no rutas públicas ni prerender (aliases → hub en firebase.json)
  loc('san-sebastian', 'San Sebastián', 'es', {
    priority: '0.8',
    hasStudents: false,
    hasEnglish: false,
    esSearch: false,
    enSearch: false,
    prerender: false,
  }),
  loc('salamanca', 'Salamanca', 'es', { priority: '0.85', hasStudents: true, hasEnglish: true }),
  loc('granada', 'Granada', 'es', { priority: '0.8', hasStudents: true, hasEnglish: true }),
  loc('zaragoza', 'Zaragoza', 'es', { priority: '0.8', hasStudents: true, hasEnglish: true }),
  loc('alicante', 'Alicante', 'es', { priority: '0.80', hasStudents: false, hasEnglish: true }),
  loc('pamplona', 'Pamplona', 'es', { priority: '0.7', hasStudents: true, hasEnglish: false }),
  loc('las-palmas', 'Las Palmas', 'es', { priority: '0.75', hasStudents: false, hasEnglish: true }),
  loc('tenerife', 'Tenerife', 'es', { priority: '0.75', hasStudents: false, hasEnglish: true, esSearch: true, enSearch: true, prerender: true }),
  loc('torremolinos', 'Torremolinos', 'es', { priority: '0.65', hasStudents: false, hasEnglish: false, esSearch: false, enSearch: true }),
  loc('gines', 'Gines', 'es', { priority: '0.65', hasStudents: false, hasEnglish: false, esSearch: false, enSearch: true }),

  // ── US — NYC ────────────────────────────────────────────────────────────
  loc('new-york', 'New York', 'us', { priority: '0.9', hasSpanish: false }),
  loc('manhattan', 'Manhattan', 'us', { priority: '0.9', hasSpanish: false }),
  loc('brooklyn', 'Brooklyn', 'us', { priority: '0.85', hasSpanish: false }),
  loc('queens-nyc', 'Queens', 'us', { priority: '0.8', hasSpanish: false }),
  loc('jersey-city', 'Jersey City', 'us', { priority: '0.8', hasSpanish: false }),
  loc('williamsburg', 'Williamsburg', 'us', { priority: '0.8', hasSpanish: false }),
  loc('astoria', 'Astoria', 'us', { priority: '0.75', hasSpanish: false }),

  // ── UK — LONDON ─────────────────────────────────────────────────────────
  loc('london', 'London', 'uk', { priority: '0.9', hasSpanish: false }),
  loc('elephant-and-castle', 'Elephant and Castle', 'uk', { priority: '0.85', hasSpanish: false }),
  loc('canary-wharf', 'Canary Wharf', 'uk', { priority: '0.8', hasSpanish: false, prerender: true }),
  loc('west-hampstead', 'West Hampstead', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('battersea', 'Battersea', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('finsbury-park', 'Finsbury Park', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('wembley', 'Wembley', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('notting-hill', 'Notting Hill', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('marylebone', 'Marylebone', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('belsize-park', 'Belsize Park', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('clapham-common', 'Clapham Common', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('maida-vale', 'Maida Vale', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('tooting', 'Tooting', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('west-kensington', 'West Kensington', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('hounslow', 'Hounslow', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('westbourne-park', 'Westbourne Park', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('primrose-hill', 'Primrose Hill', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('harrow', 'Harrow', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('earlsfield', 'Earlsfield', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('brixton', 'Brixton', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('limehouse', 'Limehouse', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('hammersmith', 'Hammersmith', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('mile-end', 'Mile End', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('canada-water', 'Canada Water', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('shoreditch', 'Shoreditch', 'uk', { priority: '0.8', hasSpanish: false }),
  loc('bloomsbury', 'Bloomsbury', 'uk', { priority: '0.8', hasSpanish: false }),
  loc('kings-cross', 'Kings Cross', 'uk', { priority: '0.8', hasSpanish: false }),
  loc('walthamstow', 'Walthamstow', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('clapham-junction', 'Clapham Junction', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('stockwell', 'Stockwell', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('east-putney', 'East Putney', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('fulham', 'Fulham', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('new-malden', 'New Malden', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('hackney', 'Hackney', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('clapham', 'Clapham', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('royal-docks', 'Royal Docks', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('canning-town', 'Canning Town', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('poplar', 'Poplar', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('stepney-green', 'Stepney Green', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('lewisham', 'Lewisham', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('hayes', 'Hayes', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('epsom', 'Epsom', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('camden', 'Camden', 'uk', { priority: '0.8', hasSpanish: false }),
  loc('islington', 'Islington', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('white-city', 'White City', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('london-bridge', 'London Bridge', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('westminster', 'Westminster', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('south-bank', 'South Bank', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('portobello', 'Portobello', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('ealing', 'Ealing', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('kingston', 'Kingston', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('whitechapel', 'Whitechapel', 'uk', { priority: '0.8', hasSpanish: false }),
  loc('bethnal-green', 'Bethnal Green', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('stratford', 'Stratford', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('holloway', 'Holloway', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('earls-court', 'Earls Court', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('deptford', 'Deptford', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('southwark', 'Southwark', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('highbury', 'Highbury', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('tottenham', 'Tottenham', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('greenwich', 'Greenwich', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('hoxton', 'Hoxton', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('vauxhall', 'Vauxhall', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('aldgate', 'Aldgate', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('bankside', 'Bankside', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('charlton', 'Charlton', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('brondesbury', 'Brondesbury', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('acton', 'Acton', 'uk', { priority: '0.75', hasSpanish: false }),
  loc('new-cross', 'New Cross', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('colindale', 'Colindale', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('southall', 'Southall', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('clerkenwell', 'Clerkenwell', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('bow', 'Bow', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('wapping', 'Wapping', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('kentish-town', 'Kentish Town', 'uk', { priority: '0.7', hasSpanish: false }),
  loc('north-acton', 'North Acton', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('south-bermondsey', 'South Bermondsey', 'uk', { priority: '0.65', hasSpanish: false }),
  loc('tufnell-park', 'Tufnell Park', 'uk', { priority: '0.65', hasSpanish: false }),
];

/** Alias slugs válidos en router (sin redirect) */
const ROUTER_ALIAS_SLUGS = [
  'donostia', 'gipuzkoa', 'guipuzcoa', 'guipúzcoa',
  'málaga',
];

function buildSlugToName() {
  const map = {};
  for (const l of LOCATIONS) map[l.slug] = l.name;
  for (const [alias, canonical] of Object.entries(SLUG_ALIASES)) {
    map[alias] = map[canonical] || LOCATIONS.find(x => x.slug === canonical)?.name || alias;
  }
  Object.assign(map, SLUG_TO_NAME_EXTRAS);
  return map;
}

const SLUG_TO_NAME = buildSlugToName();

function slugsWhere(fn) {
  return LOCATIONS.filter(fn).map(l => l.slug);
}

/** Ciudades ES principales (gestor, listados compactos) */
const VALID_CITIES_ES_MAIN = [
  'madrid', 'barcelona', 'valencia', 'sevilla', 'bilbao', 'málaga', 'malaga',
  'salamanca', 'alicante', 'granada', 'zaragoza', 'las-palmas', 'tenerife',
];

/** Ciudades ES ampliadas (coliving search ES + aliases San Sebastián) */
const VALID_CITIES_ES_EXTENDED = [
  ...new Set([
    ...slugsWhere(l => l.esSearch && l.region === 'es'),
    ...ROUTER_ALIAS_SLUGS.filter(s => s !== 'málaga'),
    'málaga',
  ]),
];

/** US slugs en rutas ES (/encuentratucoliving/manhattan — legacy) */
const VALID_CITIES_US_ON_ES = ['new-york', 'manhattan', 'brooklyn', 'jersey-city'];

/** US + UK slugs para rutas EN */
const VALID_CITIES_US = slugsWhere(l => l.region === 'us');
const VALID_CITIES_UK = slugsWhere(l => l.region === 'uk');

/** Todas las ciudades válidas en /findyourcoliving/:segment */
const VALID_CITIES_EN_FULL = [
  ...VALID_CITIES_ES_EXTENDED,
  ...VALID_CITIES_US,
  ...VALID_CITIES_UK,
];

/** /encuentratucoliving/:city */
const VALID_CITIES_ES_SEARCH = [
  ...VALID_CITIES_ES_EXTENDED,
  ...VALID_CITIES_US_ON_ES,
];

const US_CITY_SLUGS = new Set(VALID_CITIES_US);
const UK_CITY_SLUGS = new Set(VALID_CITIES_UK);

/** Formato para generate-sitemap.js — solo slugs con ruta válida en router */
function getSitemapCities() {
  const routableSlugs = new Set([
    ...VALID_CITIES_ES_SEARCH,
    ...VALID_CITIES_US,
    ...VALID_CITIES_UK,
  ]);
  return LOCATIONS.filter(l => routableSlugs.has(l.slug))
    // UK: solo barrios con 5+ listings (evita crawled-not-indexed por thin content en GSC)
    .filter(l => l.region !== 'uk' || UK_SITEMAP_SLUGS.has(l.slug))
    .map(l => ({
    name: l.slug,
    priority: l.priority,
    hasStudents: l.hasStudents,
    hasEnglish: l.hasEnglish,
    hasSpanish: l.hasSpanish,
  }));
}

/** Rutas /encuentratucoliving|findyourcoliving/:slug a prerenderizar */
function getPrerenderSearchPaths() {
  const paths = [];
  for (const l of LOCATIONS) {
    if (!l.prerender) continue;
    if (l.esSearch && l.region === 'es') {
      paths.push(`/encuentratucoliving/${l.slug}`);
    }
    // Ciudades ES bilingües: versión EN para expats / búsquedas en inglés
    if (l.enSearch && l.region === 'es' && l.hasEnglish) {
      paths.push(`/findyourcoliving/${l.slug}`);
    }
    if (l.enSearch && (l.region === 'us' || l.region === 'uk')) {
      paths.push(`/findyourcoliving/${l.slug}`);
    }
  }
  return paths;
}

const {
  RESIDENCIAS_INDEXED_CITY_SLUGS_SET,
  getResidenciasPrerenderPaths,
} = require('./residenciasSeo.cjs');

/** Ciudades indexables en /residencias/:city */
const STUDENT_INDEXED_CITY_SLUGS = RESIDENCIAS_INDEXED_CITY_SLUGS_SET;

function isStudentCitySlugSeoIndexed(citySlug) {
  if (!citySlug) return true;
  return STUDENT_INDEXED_CITY_SLUGS.has(String(citySlug).toLowerCase());
}

function getPrerenderResidenciasPaths() {
  return getResidenciasPrerenderPaths();
}

function getPrerenderStudentPaths() {
  return getPrerenderResidenciasPaths();
}

/** Páginas estáticas + legales en prerender (debe coincidir con PRERENDER_STATIC_PAGES del script) */
const PRERENDER_STATIC_PAGE_COUNT = 18;

/** Total esperado de HTML en frontend/dist/prerendered/ (usado por rebuild-and-prerender.sh) */
function getExpectedPrerenderPageCount() {
  return (
    PRERENDER_STATIC_PAGE_COUNT
    + 2 // /encuentratucoliving + /findyourcoliving
    + getPrerenderSearchPaths().length
    + getPrerenderStudentPaths().length
  );
}

/** Pares hreflang estáticos B2B (ES ↔ EN) */
const SITEMAP_STATIC_HREFLANG_GROUPS = [
  [
    { hreflang: 'es', path: '/encuentratucoliving' },
    { hreflang: 'en', path: '/findyourcoliving' },
    { hreflang: 'x-default', path: '/encuentratucoliving' },
  ],
  [
    { hreflang: 'es', path: '/software-gestion-coliving' },
    { hreflang: 'en', path: '/property-management-software' },
    { hreflang: 'x-default', path: '/software-gestion-coliving' },
  ],
  [
    { hreflang: 'es', path: '/saas-coliving' },
    { hreflang: 'en', path: '/coliving-saas' },
    { hreflang: 'x-default', path: '/saas-coliving' },
  ],
  [
    { hreflang: 'es', path: '/planes' },
    { hreflang: 'en', path: '/plans' },
    { hreflang: 'x-default', path: '/planes' },
  ],
  [
    { hreflang: 'es', path: '/calculadora-ahorro' },
    { hreflang: 'en', path: '/savings-calculator' },
    { hreflang: 'x-default', path: '/calculadora-ahorro' },
  ],
  [
    { hreflang: 'es', path: '/que-es-un-coliving' },
    { hreflang: 'en', path: '/what-is-coliving' },
    { hreflang: 'x-default', path: '/que-es-un-coliving' },
  ],
];

/**
 * Mapa path → alternates hreflang para inyectar en sitemap.xml
 * Cada URL del grupo recibe la lista completa de alternates del grupo.
 */
function getSitemapHreflangByPath() {
  const map = {};

  function registerGroup(alternates) {
    alternates.forEach(({ hreflang, path }) => {
      if (!map[path]) map[path] = [];
      const byLang = new Map(map[path].map((a) => [a.hreflang, a]));
      alternates.forEach((alt) => byLang.set(alt.hreflang, alt));
      map[path] = [...byLang.values()];
    });
  }

  SITEMAP_STATIC_HREFLANG_GROUPS.forEach(registerGroup);

  for (const city of getSitemapCities()) {
    if (city.hasEnglish && city.hasSpanish !== false) {
      registerGroup([
        { hreflang: 'es', path: `/encuentratucoliving/${city.name}` },
        { hreflang: 'en', path: `/findyourcoliving/${city.name}` },
        { hreflang: 'x-default', path: `/encuentratucoliving/${city.name}` },
      ]);
    }
  }

  return map;
}

/** Mapa slug → name para prerender (solo slugs canónicos) */
function getPrerenderCityNames() {
  const names = {};
  for (const l of LOCATIONS) names[l.slug] = l.name;
  return names;
}

function resolveCitySlug(rawSlug) {
  const normalized = rawSlug.toLowerCase();
  return SLUG_ALIASES[normalized] || normalized;
}

/** Ciudad con versión EN en /findyourcoliving/:slug (pares hreflang) */
function cityHasEnglish(rawSlug) {
  const slug = resolveCitySlug(rawSlug);
  const location = LOCATIONS.find((l) => l.slug === slug);
  return location?.hasEnglish ?? false;
}

/** Región de marketplace (es | us | uk) para un slug de ciudad en la URL */
function getRegionForCitySlug(rawSlug) {
  const slug = resolveCitySlug(rawSlug);
  const location = LOCATIONS.find((l) => l.slug === slug);
  return location?.region ?? null;
}

/** Pares hreflang B2B estáticos para prerender (misma fuente que sitemap) */
function getStaticHreflangPaths(path) {
  for (const group of SITEMAP_STATIC_HREFLANG_GROUPS) {
    if (!group.some((alt) => alt.path === path)) continue;
    return {
      esPath: group.find((alt) => alt.hreflang === 'es')?.path,
      enPath: group.find((alt) => alt.hreflang === 'en')?.path,
    };
  }
  return null;
}

function isUSCitySlug(slug) {
  return US_CITY_SLUGS.has(slug);
}

function isUKCitySlug(slug) {
  return UK_CITY_SLUGS.has(slug);
}

/** /encuentratucoliving/:provider (legacy US slugs on ES path) */
const VALID_CITIES_ES_PROVIDER = [
  ...VALID_CITIES_ES_EXTENDED,
  ...VALID_CITIES_US_ON_ES,
];

module.exports = {
  LOCATIONS,
  SLUG_ALIASES,
  SLUG_TO_NAME_EXTRAS,
  SLUG_TO_NAME,
  PRERENDER_SLUGS,
  VALID_CITIES_ES_MAIN,
  VALID_CITIES_ES_EXTENDED,
  VALID_CITIES_ES_PROVIDER,
  VALID_CITIES_US_ON_ES,
  VALID_CITIES_US,
  VALID_CITIES_UK,
  VALID_CITIES_EN_FULL,
  VALID_CITIES_ES_SEARCH,
  US_CITY_SLUGS,
  UK_CITY_SLUGS,
  getSitemapCities,
  getPrerenderSearchPaths,
  getPrerenderStudentPaths,
  getExpectedPrerenderPageCount,
  PRERENDER_STATIC_PAGE_COUNT,
  STUDENT_INDEXED_CITY_SLUGS,
  isStudentCitySlugSeoIndexed,
  getSitemapHreflangByPath,
  SITEMAP_STATIC_HREFLANG_GROUPS,
  getPrerenderCityNames,
  resolveCitySlug,
  cityHasEnglish,
  getRegionForCitySlug,
  getStaticHreflangPaths,
  isUSCitySlug,
  isUKCitySlug,
};
